import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { useQuery, useMutation } from '@apollo/react-hooks';
import {
  Box,
  Button,
  Heading,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalOverlay,
  Text,
  VStack,
  useToast,
  Input,
  Select,
  Badge,
} from '@chakra-ui/react';
import { FaTrash } from 'react-icons/fa';

import { Provider } from '../../Chakra';
import useHeaderComponentsMutation from '../../Hooks/useHeaderComponentsMutation';
import useCurrentUser from '../../Hooks/userCurrentUser';

const FETCH_REGION = gql`
  query fetchRegions($userId: Int!){
    fetchRegions(userId: $userId) {
      id
      name
      regionsUsers {
        id
        roleId
        fetchUser {
          id
          firstname
          lastname
          email
          profileImageUrl
        }
      }
      invitedRegionsUsers {
        id
        email
        invitationCode
      }
    }
  }
`;

const ADD_REGION_ADMIN = gql`
  mutation addRegionUser($regionId: Int!, $email: String!) {
    addRegionUser(input: {
      regionId: $regionId,
      email: $email,
      roleId: 2,
    }) {
      result
    }
  }
`;

const REMOVE_REGION_ADMIN = gql`
  mutation removeRegionUser($regionId: Int!, $userId: Int!) {
    removeRegionUser(input: {
      regionId: $regionId,
      userId: $userId,
    }) {
      result
    }
  }
`;

const UserPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  firstname: PropTypes.string.isRequired,
  lastname: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  profileImageUrl: PropTypes.string,
});
const RegionPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  regionsUsers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    roleId: PropTypes.number.isRequired,
    fetchUser: UserPropType.isRequired,
  })).isRequired,
});

const DeleteUserModal = ({ isOpen, onClose, userId, regionId, userName }) => {
  const toast = useToast();
  const [input, setInput] = useState();
  const [removeRegionalAdmin, { loading }] = useMutation(REMOVE_REGION_ADMIN, {
    variables: {
      regionId: parseInt(regionId, 10),
      userId: parseInt(userId, 10),
    },
    refetchQueries: ['fetchRegions'],
    awaitRefetchQueries: true,
    onError: (error) => {
      toast({
        title: error.graphQLErrors?.[0]?.message ?? error.message,
        status: 'error',
        isClosable: true,
        position: 'top',
      });
    },
    onCompleted: () => {
      toast({
        title: 'Regional admin removed successfully',
        status: 'success',
        isClosable: true,
        position: 'top',
      });
    },
  });

  const message = `Are you sure you want to remove ${userName} as a regional admin? Type DELETE to confirm.`;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Delete Regional Admin</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack>
            <Text>
              {message}
            </Text>
            <Input value={input} onChange={(e) => setInput(e.target.value)} />
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="red" mr={3} onClick={removeRegionalAdmin} isLoading={loading} disabled={input !== 'DELETE'}>
            Delete Regional Admin
          </Button>
          <Button colorScheme="gray" variant="outline" mr={3} onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
DeleteUserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  regionId: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
};

const UserRow = ({ regionId, user, isSuperAdmin, canDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const onClose = () => setIsOpen(false);

  const name = `${user.firstname} ${user.lastname}`;
  return (
    <>
      <HStack spacing={2} border="1px solid #E2E8F0" padding={2} borderRadius="md" width="100%">
        <Image src={user.profileImageUrl} borderRadius="full" boxSize="50px" />
        <VStack align="start" spacing={0}>
          <HStack>
            <Text>{name}</Text>
            {isSuperAdmin && <Badge colorScheme="orange">Super Admin</Badge>}
          </HStack>
          <Text>{user.email}</Text>
        </VStack>
        <Box flexGrow={1} />
        <IconButton
          variant="ghost"
          aria-label="Delete User"
          colorScheme="red"
          icon={<FaTrash />}
          disabled={!canDelete}
          onClick={() => {
            setIsOpen(true);
          }}
        />
      </HStack>
      {isOpen && (
        <DeleteUserModal
          isOpen={isOpen}
          onClose={onClose}
          userId={user.id}
          regionId={regionId}
          userName={name}
        />
      )}
    </>
  );
};
UserRow.propTypes = {
  regionId: PropTypes.string.isRequired,
  user: UserPropType.isRequired,
  isSuperAdmin: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool.isRequired,
};

const RegionSection = ({ region, currentUserId }) => {
  const regionsUsers = region.regionsUsers.toSorted((a, b) => {
    const res = a.fetchUser.firstname.localeCompare(b.fetchUser.firstname);
    if (res === 0) {
      return a.fetchUser.lastname.localeCompare(b.fetchUser.lastname);
    }
    return res;
  });

  return (
    <VStack spacing={4} align="start" width="100%" maxWidth="500px" marginBlockEnd={4}>
      <Heading size="md">{region.name}</Heading>
      <VStack spacing={4} align="start" width="100%">
        {regionsUsers.map((ru) => (
          <UserRow
            key={ru.fetchUser.id}
            regionId={region.id}
            user={ru.fetchUser}
            isSuperAdmin={ru.roleId === 1}
            canDelete={currentUserId !== ru.fetchUser.id}
          />
        ))}
      </VStack>
      {region.invitedRegionsUsers.length > 0 && (
        <>
          <Heading size="sm">Invited Admins</Heading>
          <VStack spacing={4} align="start" width="100%">
            {region.invitedRegionsUsers.map((iru) => (
              <HStack key={iru.id} spacing={2} border="1px solid #E2E8F0" padding={2} borderRadius="md" width="100%">
                <VStack align="start" spacing={0}>
                  <Text>{iru.email}</Text>
                </VStack>
              </HStack>
            ))}
          </VStack>
        </>
      )}
    </VStack>
  );
};
RegionSection.propTypes = RegionPropType.isRequired;

const AddAdminForm = ({ onClose, regions }) => {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [selectedRegionId, setSelectedRegionId] = useState(regions[0]?.id);
  const [addRegionalAdmin, { loading }] = useMutation(ADD_REGION_ADMIN, {
    variables: {
      regionId: parseInt(selectedRegionId, 10),
      email,
    },
    refetchQueries: ['fetchRegions'],
    awaitRefetchQueries: true,
    onError: (error) => {
      toast({
        title: error.graphQLErrors?.[0]?.message ?? error.message,
        status: 'error',
        isClosable: true,
        position: 'top',
      });
    },
    onCompleted: (res) => {
      toast({
        title: res.addRegionUser.result,
        status: 'success',
        isClosable: true,
        position: 'top',
      });
      setEmail('');
      onClose();
    },
  });

  return (
    <VStack width="100%" maxWidth="500px" border="1px solid #E2E8F0" borderRadius="md" padding={4} spacing={4}>
      <Text>Add New Admin</Text>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          addRegionalAdmin();
        }}
        style={{
          width: '100%',
        }}
      >
        <Input id="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email Address" />
        {regions.length > 1 && (
          <Select
            id="region"
            value={selectedRegionId}
            onChange={(e) => setSelectedRegionId(e.target.value)}
            marginBlockStart={4}
          >
            {regions.map((region) => (
              <option key={region.id} value={region.id}>{region.name}</option>
            ))}
          </Select>
        )}
        <HStack width="100%" spacing={4} marginBlockStart={4} justifyContent="flex-end">
          <Button onClick={onClose} disabled={loading}>Close</Button>
          <Button type="submit" colorScheme="blue" isLoading={loading} disabled={!email || !selectedRegionId}>Add Admin</Button>
        </HStack>
      </form>
    </VStack>
  );
};
AddAdminForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  regions: PropTypes.arrayOf(RegionPropType).isRequired,
};

const AdminPageContent = () => {
  const {
    currentUser,
    loading: userLoading,
    error: userError,
  } = useCurrentUser(true);
  useHeaderComponentsMutation({
    title: 'Admins',
    backLink: '/dashboard',
  });

  const { data, loading } = useQuery(FETCH_REGION, {
    name: 'fetchRegions',
    variables: { userId: parseInt(currentUser?.id, 10) || 0 },
    skip: userLoading || userError,
  });

  const [isFormOpen, setIsFormOpen] = useState(false);

  if (loading || userLoading) {
    return <div>Loading...</div>;
  }

  return (
    <VStack spacing={4} paddingBlockStart={4} width="100%">
      {isFormOpen ? (
        <AddAdminForm onClose={() => setIsFormOpen(false)} regions={data.fetchRegions} />
      ) : (
        <Button colorScheme="blue" onClick={() => setIsFormOpen(true)}>Add New Admin</Button>
      )}

      {data.fetchRegions.map((region) => (
        <RegionSection key={region.id} region={region} currentUserId={currentUser.id} />
      ))}
    </VStack>
  );
};

export const AdminPage = () => (
  <Provider>
    <AdminPageContent />
  </Provider>
);
